import { Routes } from '@angular/router';
import { BlankComponent } from './layouts/blank/blank.component';
import { FullComponent } from './layouts/full/full.component';
import { AuthGuard } from './auth.guard';
import { HospitalComponent } from './pages/hospital/hospital.component';
import { UserProfileComponent } from './pages/settings/user-profile/user-profile.component';
import { HospitalSettingComponent } from './pages/settings/hospital-setting/hospital-setting.component';
import { SettingTabComponent } from './pages/settings/setting-tab/setting-tab.component';
import { BillChargeResolver } from './pages/settings/hospital-setting/bill-charge/bill-charge.resolver';
import { DoctorSpecialityResolver } from './pages/settings/hospital-setting/doctor-speciality/doctor-speciality.resolver';
import { DoctorDropDownResolver } from './resolvers/doctorDropDown.resolver';
import { BillchargeDropDownResolver } from './resolvers/billchargeDropDown.resolver';
import { IpdTabComponent } from './pages/settings/ipd/ipdTab.component';
import { WardResolver } from './pages/settings/ipd/ward/ward.resolver';
import { RoomTypeResolver } from './pages/settings/ipd/room-type/room-type.resolver';
import { WardDropDownResolver } from './resolvers/wardDropDown.resolver';
import { RoomResolver } from './pages/settings/ipd/room/room.resolver';
import { BedResolver } from './pages/settings/ipd/bed/bed.resolver';
import { IpdComponent } from './pages/ipd/ipd.component';
import { ipdFeatureGuard } from './guards/ipd-feature.guard';
import { pharmacyFeatureGuard } from './guards/pharmacy-feature.guard';
import { DashBoardComponent } from './pages/dashboard/dashboard.component';
import { IpdManagementComponent } from './pages/ipd/ipd-management/ipd-management.component';
import { roleGuard } from './guards/role.guard';
import { ROLE_GROUPS } from './common/authorization/roles';

export const routes: Routes = [
  {
    // Onboarding screen. AuthGuard sends anonymous visitors to login; the role guard
    // keeps it to SuperAdmin while self-registration is closed.
    path: 'hospital',
    component: HospitalComponent,
    canActivate: [AuthGuard, roleGuard],
    data: {
      title: 'Hospital',
      allowedRoles: ROLE_GROUPS.PlatformAdmin,
    },
  },
  {
    path: '',
    component: FullComponent,
    canActivate: [AuthGuard, roleGuard],
    canActivateChild: [AuthGuard, roleGuard],
    data: { allowedRoles: ROLE_GROUPS.TenantAndPharmacy },
    children: [
      {
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        component: DashBoardComponent,
        canActivate: [roleGuard],
        title: 'Dashboard',
        data: { allowedRoles: ROLE_GROUPS.TenantAndPharmacy },
      },
      {
        path: 'patient',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.AllTenant },
        loadChildren: () =>
          import('./pages/patient/patient.route').then(m => m.patientRoutes),
      },
      {
        path: 'opd',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.AllTenant },
        loadChildren: () =>
          import('./pages/opd/opd.route').then(m => m.opdRoute),
      },
      {
        path: 'appointment',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.AllTenant },
        loadChildren: () =>
          import('./pages/appointment/appointment.route').then(m => m.appointmentRoutes),
      },
      {
        path: 'reports',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.AllTenant },
        loadChildren: () =>
          import('./pages/reports/reports.route').then(m => m.reportsRoutes),
      },
      {
        path: 'pharmacy',
        canActivate: [pharmacyFeatureGuard, roleGuard],
        data: { allowedRoles: ROLE_GROUPS.Pharmacy },
        loadChildren: () =>
          import('./pages/pharmacy/pharmacy.route').then(m => m.pharmacyRoutes),
      },
      {
        path: 'user',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.AdminOnly },
        loadChildren: () =>
          import('./pages/user/user.route').then((m) => m.userRoutes),
      },
      {
        path: 'clinical',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.AllTenant },
        loadChildren: () =>
          import('./pages/category/category.route').then(m => m.categoryroutes),
      },
      {
        path: 'Mediclaim',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.AllTenant },
        loadChildren: () =>
          import('./pages/settings/mediclaim/mediclaim.route').then((m) => m.mediclaimRoutes),
      },
      {
        path: 'appointmenttab',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.AllTenant },
        loadChildren: () =>
          import('./pages/settings/appointment-tab/appointment-tab.route').then((m) => m.appointmenttabRoutes),
      },
      {
        path: 'Drug',
        canActivate: [roleGuard],
        data: { allowedRoles: ROLE_GROUPS.TenantAndPharmacy },
        loadChildren: () =>
          import('./pages/settings/drug-tab/drug-tab.route').then((m) => m.drugtabRoutes),
      },
      {
        path: 'userprofile',
        component: UserProfileComponent,
        canActivate: [roleGuard],
        title: 'Userprofile',
        data: { allowedRoles: ROLE_GROUPS.TenantAndPharmacy },
      },
      {
        path: 'hospitalsetting',
        canActivate: [roleGuard],
        component: HospitalSettingComponent,
        resolve: {
          billChargedata: BillChargeResolver,
          doctorSpecialityData: DoctorSpecialityResolver,
        },
        data: {
          title: 'Hospital',
          allowedRoles: ROLE_GROUPS.TenantAndPharmacy,
          urls: [
            { title: 'Settings', url: '/settings' },
            { title: 'Hospital' },
          ],
        },
      },
      {
        path: 'settings',
        canActivate: [roleGuard],
        component: SettingTabComponent,
        title: 'Settings', // header Title
        data: {
          title: 'Settings', // breadcrumb Title
          allowedRoles: ROLE_GROUPS.TenantAndPharmacy,
          urls: [
            { title: 'Settings' },
          ],
        },
      },
      {
        path: 'ipd/management',
        component: IpdManagementComponent,
        canActivate: [ipdFeatureGuard, roleGuard],
        resolve: {
          ddlward: WardDropDownResolver,
          ddlDoctor: DoctorDropDownResolver,
        },
        title: 'IPD Management',
        data: { allowedRoles: ROLE_GROUPS.AllTenant },
      },
      {
        path: 'ipd',
        component: IpdTabComponent,
        canActivate: [ipdFeatureGuard, roleGuard],
        resolve: {
          warddata: WardResolver,
        },
        title: 'IPD',
        data: {
          title: 'IPD',
          allowedRoles: ROLE_GROUPS.AllTenant,
          urls: [
            { title: 'Settings', url: '/settings' },
            { title: 'IPD' }
          ],
        },
      },
      {
        path: 'ipd/ward/:wardId',
        component: IpdTabComponent,
        canActivate: [ipdFeatureGuard, roleGuard],
        resolve: {
          roomtypeData: RoomTypeResolver,
          ddlbillcharge: BillchargeDropDownResolver,
        },
        title: 'Room Type',
        data: {
          title: 'IPD',
          allowedRoles: ROLE_GROUPS.AllTenant,
          urls: [
            { title: 'Settings', url: '/settings' },
            { title: 'IPD' }
          ],
        },
      },
      {
        path: 'ipd/ward/:wardId/Roomtype/:roomTypeId',
        component: IpdTabComponent,
        canActivate: [ipdFeatureGuard, roleGuard],
        resolve: {
          roomdata: RoomResolver,
        },
        title: 'Room',
        data: {
          title: 'IPD',
          allowedRoles: ROLE_GROUPS.AllTenant,
          urls: [
            { title: 'Settings', url: '/settings' },
            { title: 'IPD' }
          ],
        },
      },
      {
        path: 'ipd/ward/:wardId/Roomtype/:roomTypeId/Room/:roomId',
        component: IpdTabComponent,
        canActivate: [ipdFeatureGuard, roleGuard],
        resolve: {
          beddata: BedResolver,
        },
        title: 'Bed',
        data: {
          title: 'IPD',
          allowedRoles: ROLE_GROUPS.AllTenant,
          urls: [
            { title: 'Settings', url: '/settings' },
            { title: 'IPD' }
          ],
        },
      },
      {
        path: 'ipd/admit/:ipdId/patient/:patientId',
        component: IpdComponent,
        canActivate: [ipdFeatureGuard, roleGuard],
        resolve: {
          ddlbillcharge: BillchargeDropDownResolver,
        },
        title: 'IPD',
        data: {
          title: 'IPD',
          allowedRoles: ROLE_GROUPS.AllTenant,
          urls: [
            { title: 'Settings', url: '/settings' },
            { title: 'IPD' }
          ],
        },
      }
    ],
  },
  {
    path: '',
    component: BlankComponent,
    children: [
      {
        path: 'authentication',
        loadChildren: () =>
          import('./pages/authentication/authentication.routes').then(
            (m) => m.AuthenticationRoutes
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'authentication/error404',
    pathMatch: 'full',
  },

];
